import { useEffect, useState } from 'react';

import { getApiClient } from '@apps/web/lib/api_client';
import { isTransparentRateLimitError } from '@apps/web/lib/known_api_errors';
import { useMode } from '@apps/web/lib/mode_store';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';
import {
    type ApiError,
    type ApiResponseType,
    isAbortError,
    type Mode,
} from '@libraries/typescript/api_client';

export function useCoverageMapPoints({
    organizationId,
    enabled,
    includeInactive,
    includeDoNotContact,
}: {
    readonly organizationId: string | null;
    readonly enabled: boolean;
    readonly includeInactive: boolean;
    readonly includeDoNotContact: boolean;
}) {
    return useApiQuery({
        path: '/organizations/{organization_id}/coverage/map',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: {
            ...(includeInactive ? { include_inactive: true } : {}),
            ...(includeDoNotContact ? { include_do_not_contact: true } : {}),
        },
        enabled: enabled && !!organizationId,
        select: (response) => response,
        reason: 'coverage-map',
    });
}

// Only statuses change minute to minute. Poll this small read instead of the
// full map response so a refresh never repeats the roster and geocode work.
export function useCoverageMapAvailability({
    organizationId,
    enabled,
    includeInactive,
}: {
    readonly organizationId: string | null;
    readonly enabled: boolean;
    readonly includeInactive: boolean;
}) {
    return useApiQuery({
        path: '/organizations/{organization_id}/coverage/map/availability',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: {
            ...(includeInactive ? { include_inactive: true } : {}),
        },
        enabled: enabled && !!organizationId,
        select: (response) => response,
        reason: 'coverage-map-availability',
        // The map response already carries first-paint statuses, so the
        // first poll waits a full interval instead of repeating that work.
        revalidateOnMount: false,
        refreshInterval: enabled ? 60_000 : 0,
        refreshWhenHidden: false,
    });
}

type ClientsResponse = ApiResponseType<
    '/organizations/{organization_id}/coverage/map/clients',
    'get'
>;

type ClientsScope = {
    readonly organizationId: string;
    readonly mode: Mode | undefined;
    readonly accessKey?: string;
};

type ClientsState = ClientsScope & {
    readonly data?: ClientsResponse;
    readonly error?: ApiError;
};

export type CoverageMapClients = {
    readonly data: ClientsResponse | undefined;
    readonly error: ApiError | undefined;
    readonly isLoading: boolean;
    readonly retry: () => void;
};

function sameScope(
    state: ClientsState | undefined,
    scope: ClientsScope,
): state is ClientsState {
    return (
        state?.organizationId === scope.organizationId &&
        state.mode === scope.mode &&
        state.accessKey === scope.accessKey
    );
}

// Client locations are sensitive. Keep it outside SWR so disabling the layer
// aborts an in-flight request and releases the response. Call this from a
// component that stays mounted while the user moves between coverage views:
// every remount is a new request against a small per-member budget.
export function useCoverageMapClients({
    organizationId,
    enabled,
    accessKey,
}: {
    readonly organizationId: string | null;
    readonly enabled: boolean;
    readonly accessKey?: string;
}): CoverageMapClients {
    const apiClient = getApiClient();
    const mode = useMode();
    const [state, setState] = useState<ClientsState>();
    const [attempt, setAttempt] = useState(0);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        let active = true;
        if (!enabled || !organizationId) {
            setState(undefined);
            setIsLoading(false);
            return;
        }

        const scope: ClientsScope = { organizationId, mode, accessKey };
        // A retry keeps the stars already on the map; a scope change must
        // never show another organization's or permission's clients.
        setState((current) =>
            sameScope(current, scope)
                ? { ...current, error: undefined }
                : undefined,
        );
        const controller = new AbortController();
        setIsLoading(true);
        void apiClient
            .get({
                url: '/organizations/{organization_id}/coverage/map/clients',
                pathParams: { organization_id: organizationId },
                mode,
                reason: 'coverage-map-clients',
                signal: controller.signal,
            })
            .then((response) => {
                if (active) {
                    setState({ ...scope, data: response });
                    setIsLoading(false);
                }
            })
            .catch((requestError: unknown) => {
                if (!active || isAbortError(requestError)) {
                    return;
                }
                setState((current) => {
                    const data = sameScope(current, scope)
                        ? current.data
                        : undefined;
                    // A transparent rate limit is hidden only while there is
                    // something to show; with nothing loaded, offer a retry.
                    if (data && isTransparentRateLimitError(requestError)) {
                        return { ...scope, data };
                    }
                    return { ...scope, data, error: requestError as ApiError };
                });
                setIsLoading(false);
            });

        return () => {
            active = false;
            controller.abort();
        };
    }, [apiClient, enabled, mode, organizationId, accessKey, attempt]);

    const currentState =
        enabled &&
        organizationId &&
        sameScope(state, { organizationId, mode, accessKey })
            ? state
            : undefined;
    return {
        data: currentState?.data,
        error: currentState?.error,
        isLoading,
        retry: () => setAttempt((value) => value + 1),
    };
}

export function useCoverageAvailability({
    organizationId,
    weekStart,
    includeInactive,
    includeDoNotContact,
}: {
    readonly organizationId: string | null;
    readonly weekStart: string | undefined;
    readonly includeInactive: boolean;
    readonly includeDoNotContact: boolean;
}) {
    return useApiQuery({
        path: '/organizations/{organization_id}/coverage',
        method: 'get',
        pathParams: organizationId
            ? { organization_id: organizationId }
            : undefined,
        queryParams: {
            ...(weekStart ? { week_start: weekStart } : {}),
            ...(includeInactive ? { include_inactive: true } : {}),
            ...(includeDoNotContact ? { include_do_not_contact: true } : {}),
        },
        enabled: !!organizationId,
        select: (response) => response,
        // Week and cohort toggles each key a fresh cache entry, so without
        // this every first toggle blanks the grid to a skeleton.
        keepPreviousData: true,
        reason: 'coverage-dashboard',
    });
}
